import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  B2bInvitationData,
  B2bInvitationDialogComponent,
} from '../../standalone-components/dialogs/b2b-invitation-dialog/b2b-invitation-dialog.component';
import { ChannelsService } from '../api/channels.service';
import { SharedChannelCategory } from '../model/sharedChannelCategory';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class B2bInvitationDialogService {
  private dialog = inject(MatDialog);
  private channelsService = inject(ChannelsService);
  private authService = inject(AuthService);

  // Flag para controlar si hay un dialog abierto
  private isDialogOpen = false;

  // Flag para controlar que solo se ejecute una vez por sesiИn
  private b2bInvitationInitialized = false;

  constructor() {}

  /**
   * Procesa un array de invitaciones B2B y muestra el dialog si es necesario
   * Controla la lИgica de ejecuciИn カnica por sesiИn
   */
  processInvitations(invitationsArray: B2bInvitationData[]): void {
    // Evitar ejecuciones mカltiples en la misma sesiИn
    if (this.b2bInvitationInitialized) {
      console.log('Invitaciones B2B ya procesadas en esta sesiИn');
      return;
    }

    console.log('Procesando invitaciones B2B:', invitationsArray);

    // La API devuelve un array de invitaciones
    if (Array.isArray(invitationsArray) && invitationsArray.length > 0) {
      // Procesar la primera invitaciИn pendiente del array
      const firstInvitation = invitationsArray[0];

      console.log('Primera invitaciИn encontrada:', firstInvitation);

      // Verificar que tenemos datos vケlidos antes de mostrar el dialog
      if (
        firstInvitation &&
        firstInvitation.id &&
        firstInvitation.template &&
        firstInvitation.template.header &&
        firstInvitation.status === 'Pending'
      ) {
        console.log('Mostrando dialog de invitaciИn B2B');
        this.showInvitationDialog(firstInvitation);
      } else {
        console.log('La primera invitaciИn no es vケlida:', {
          hasId: !!firstInvitation?.id,
          hasTemplate: !!firstInvitation?.template,
          hasHeader: !!firstInvitation?.template?.header,
          status: firstInvitation?.status,
          invitation: firstInvitation,
        });
      }

      // Marcar como inicializado independientemente del resultado
      this.b2bInvitationInitialized = true;
    } else {
      console.log(
        'No hay invitaciones B2B pendientes en el array:',
        invitationsArray
      );
      this.b2bInvitationInitialized = true;
    }
  }

  /**
   * Mゼtodo privado para mostrar el dialog con los datos proporcionados
   */
  private showInvitationDialog(data: B2bInvitationData): void {
    // Prevenir mカltiples dialogs abiertos simultケneamente
    if (this.isDialogOpen) {
      console.log('Ya hay un dialog B2B abierto, ignorando nueva solicitud');
      return;
    }

    this.isDialogOpen = true;

    const dialogRef = this.dialog.open(B2bInvitationDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: false,
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('Dialog result:', result);

      // Marcar el dialog como cerrado
      this.isDialogOpen = false;

      // Procesar la respuesta del usuario
      this.handleDialogResponse(result, data);
    });
  }

  /**
   * Maneja la respuesta del usuario en el dialog
   */
  private handleDialogResponse(
    result: string | undefined,
    data: B2bInvitationData
  ): void {
    switch (result) {
      case 'accept':
        console.log('Usuario aceptИ la invitaciИn para relationId:', data.id);
        this.acceptInvitation(data);
        break;
      case 'reject':
        console.log('Usuario rechazИ la invitaciИn para relationId:', data.id);
        this.rejectInvitation(data);
        break;
      case 'continue':
        console.log(
          'Usuario eligiИ continuar sin unirse - no se actualiza estado de invitaciИn'
        );
        // La invitaciИn permanece como "Pending" y podrケ ser procesada mケs tarde
        break;
      default:
        console.log('Dialog cerrado sin seleccionar una opciИn especヴfica');
      // No se realiza ninguna acciИn en la API
    }
  }

  /**
   * Acepta una invitaciИn B2B
   */
  private acceptInvitation(data: B2bInvitationData): void {
    const userId = this.authService.userProfile?.info?.sub;

    if (!userId) {
      console.error(
        'No se puede aceptar la invitaciИn: Usuario no autenticado'
      );
      return;
    }

    console.log('Enviando aceptaciИn de invitaciИn:', {
      relationId: data.id,
      userId: userId,
    });

    this.channelsService
      .channelsAcceptChannelInvitation(
        data.id,
        data.category as SharedChannelCategory | undefined
      )
      .subscribe({
        next: (response) => {
          console.log('InvitaciИn aceptada exitosamente:', response);
        },
        error: (error) => {
          console.error('Error al aceptar invitaciИn:', error);
        },
      });
  }

  /**
   * Rechaza una invitaciИn B2B
   */
  private rejectInvitation(data: B2bInvitationData): void {
    const userId = this.authService.userProfile?.info?.sub;

    if (!userId) {
      console.error(
        'No se puede rechazar la invitaciИn: Usuario no autenticado'
      );
      return;
    }

    console.log('Enviando rechazo de invitaciИn:', { relationId: data.id });

    this.channelsService
      .channelsRejectChannelInvitation(
        data.id,
        data.category as SharedChannelCategory | undefined
      )
      .subscribe({
        next: (response) => {
          console.log('InvitaciИn rechazada exitosamente:', response);
        },
        error: (error) => {
          console.error('Error al rechazar invitaciИn:', error);
        },
      });
  }

  /**
   * Verifica si hay un dialog abierto
   */
  isDialogCurrentlyOpen(): boolean {
    return this.isDialogOpen;
  }

  /**
   * Reset del estado de inicializaciИn (カtil para testing o reinicio manual)
   */
  resetSessionState(): void {
    this.b2bInvitationInitialized = false;
    console.log('Estado de sesiИn B2B reiniciado');
  }

  /**
   * Obtiene el estado actual del servicio
   */
  getServiceState(): { isDialogOpen: boolean; isInitialized: boolean } {
    return {
      isDialogOpen: this.isDialogOpen,
      isInitialized: this.b2bInvitationInitialized,
    };
  }
}
